import { createTheme, responsiveFontSizes, alpha } from '@mui/material/styles';
import '../../assets/fonts/satoshi.css';

// 1. Modern Color Palette
const palette = {
  mode: 'light',
  // Common colors
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  // 60% - Dominant Color (Primary)
  primary: {
    light: '#E6E6FA', // Lavender - lighter shade
    main: '#9370DB', // Medium Purple - dominant color (60%)
    dark: '#663399', // Rebecca Purple - darker shade
    text: '#9370DB',
    contrastText: '#FFFFFF',
  },
  // 30% - Secondary Color
  secondary: {
    light: '#D8BFD8', // Thistle - lighter purple
    main: '#DDA0DD', // Plum - secondary color (30%)
    dark: '#BA55D3', // Medium Orchid - darker shade
    contrastText: '#FFFFFF',
  },
  // 10% - Accent Colors
  success: {
    light: '#98FB98',
    main: '#3CB371', // Soft green that complements lavender
    dark: '#2E8B57',
    contrastText: '#FFFFFF',
  },
  warning: {
    light: '#FFE4B5',
    main: '#FFA07A', // Soft coral that complements lavender
    dark: '#FF7F50',
    contrastText: '#FFFFFF',
  },
  error: {
    light: '#FFB6C1',
    main: '#FF69B4', // Soft pink that complements lavender
    dark: '#DB7093',
    contrastText: '#FFFFFF',
  },
  info: {
    light: '#B0E0E6',
    main: '#87CEEB', // Soft blue that complements lavender
    dark: '#4682B4',
    contrastText: '#FFFFFF',
  },
  neutral: {
    light: '#E6E6FA',
    main: '#D8BFD8', // Thistle - neutral lavender
    dark: '#9370DB',
    contrastText: '#FFFFFF',
  },
  // semantic tokens with refined colors
  appointment: {
    main: '#3CB371', // Soft green
  },
  patient: {
    main: '#9370DB', // Primary lavender
  },
  alert: {
    main: '#FF69B4', // Soft pink
  },
  background: {
    default: '#F8F8FF', // Ghost White - very light lavender tint
    paper: '#FFFFFF',
    subtle: '#E6E6FA', // Lavender background
  },
  divider: 'rgba(147, 112, 219, 0.12)',
  text: {
    primary: '#2C1810', // Deep brown for contrast
    secondary: '#4A3B52', // Muted purple-brown
    disabled: '#B8A8C3', // Muted lavender
  },
};

// 2. Modern Typography System
const typography = {
  fontFamily: "'Satoshi', sans-serif",
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: { 
    fontSize: '2rem', 
    fontWeight: 500, 
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h2: { 
    fontSize: '2rem', 
    fontWeight: 700, 
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h3: { 
    fontSize: '1.5rem', 
    fontWeight: 600, 
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  h4: { 
    fontSize: '1.2rem', 
    fontWeight: 600, 
    lineHeight: 1.4
  },
  h5: { 
    fontSize: '1.125rem', 
    fontWeight: 600, 
    lineHeight: 1.5
  },
  h6: { 
    fontSize: '0.9rem', 
    fontWeight: 600, 
    lineHeight: 1.5
  },
  subtitle1: { 
    fontSize: '1rem', 
    fontWeight: 500,
    lineHeight: 1.5,
    color: palette.text.secondary,
  },
  subtitle2: { 
    fontSize: '0.85rem', 
    fontWeight: 500,
    lineHeight: 1.57,
    color: palette.text.secondary,
  },
  body1: { 
    fontSize: '1rem', 
    lineHeight: 1.6,
    letterSpacing: '0.01em',
    
  },
  body2: { 
    fontSize: '0.85rem', 
    fontWeight: 300,
    lineHeight: 1.57,
    letterSpacing: '0.01em',

  },
  button: { 
    fontSize: '0.875rem', 
    fontWeight: 600, 
    letterSpacing: '0.02em',
    textTransform: 'none',
  },
  caption: { 
    fontSize: '0.75rem', 
    lineHeight: 1.5,
    color: palette.text.secondary,
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    lineHeight: 1.6,
    textTransform: 'uppercase',
  },
};

// 3. Enhanced shape, spacing, shadows
const shape = { borderRadius: 12 }; // Smoother, more modern Apple-like design
const spacing = 8; // Keep 8px as base unit
const shadows = [
  'none',
  '0px 1px 2px rgba(0,0,0,0.06)',
  '0px 2px 4px rgba(0,0,0,0.08)',
  '0px 4px 8px rgba(0,0,0,0.12)',
  '0px 8px 16px rgba(0,0,0,0.08)',
  '0px 12px 24px rgba(0,0,0,0.12)',
  '0px 14px 28px rgba(0,0,0,0.15)',
  '0px 16px 32px rgba(0,0,0,0.18)',
  '0px 20px 40px rgba(0,0,0,0.20)',
  '0px 24px 48px rgba(0,0,0,0.25)',
  '0px 28px 56px rgba(0,0,0,0.30)',
  '0px 32px 64px rgba(0,0,0,0.35)',
  '0px 36px 72px rgba(0,0,0,0.40)',
  '0px 40px 80px rgba(0,0,0,0.45)',
  '0px 44px 88px rgba(0,0,0,0.50)',
  '0px 48px 96px rgba(0,0,0,0.55)',
  '0px 52px 104px rgba(0,0,0,0.60)',
  '0px 56px 112px rgba(0,0,0,0.65)',
  '0px 60px 120px rgba(0,0,0,0.70)',
  '0px 64px 128px rgba(0,0,0,0.75)',
  '0px 68px 136px rgba(0,0,0,0.80)',
  '0px 72px 144px rgba(0,0,0,0.85)',
  '0px 76px 152px rgba(0,0,0,0.90)',
  '0px 80px 160px rgba(0,0,0,0.95)',
  '0px 84px 168px rgba(0,0,0,1.0)',
];
// 4. Component Overrides
const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 600,
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },

      contained: {
        boxShadow: 'none',
        background: `linear-gradient(135deg, ${alpha(palette.primary.main, 1)} , ${alpha(palette.primary.main, 1)} )`,
        backdropFilter: 'blur(10px)', 
        borderRadius: '12px',
        WebkitBackdropFilter: 'blur(10px)',
        color: palette.common.white,
        border: `1px solid ${alpha(palette.common.white, 0.2)}`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.2)} 0%, transparent 100%)`,
          borderRadius: 'inherit',
          pointerEvents: 'none'
        },
        '&:hover': {
          boxShadow: 'none',
          background: `linear-gradient(135deg, ${alpha(palette.primary.main, 1)} 20%, ${alpha(palette.secondary.main, 1)} 100%)`,
          transform: 'translateY(-1px)',
          transition: 'all 0.2s ease-in-out'
        },
      },
      outlined: {
        borderWidth: '1.5px',
        borderColor: alpha(palette.primary.main, 0.3),
        background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.1)} 0%, ${alpha(palette.common.white, 0.05)} 100%)`,
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.1)} 0%, transparent 100%)`,
          borderRadius: 'inherit',
          pointerEvents: 'none'
        },
        '&:hover': {
          borderWidth: '1.5px',
          borderColor: alpha(palette.primary.main, 0.5),
          background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.15)} 0%, ${alpha(palette.common.white, 0.1)} 100%)`,
          transform: 'translateY(-1px)',
          transition: 'all 0.2s ease-in-out'
        },
      },
      text: {
        '&:hover': {
          background: alpha(palette.primary.main, 0.04),
        },
      },
      sizeSmall: {
        padding: '4px 12px',
        fontSize: '0.8125rem',
      },
      sizeMedium: {
        padding: '6px 16px',
        fontSize: '0.875rem',
      },
      sizeLarge: {
        padding: '8px 20px',
        fontSize: '0.9375rem',
      },
    },
    defaultProps: {
      disableElevation: true,
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 3,
        boxShadow: shadows[3],
        background: `linear-gradient(135deg, ${alpha(palette.primary.main, 0.1)} 20%, ${alpha(palette.secondary.light, 0.1)} 100%)`,
        backdropFilter: 'blur(5px)',
        border: `1px solid ${alpha(palette.common.white, 0.1)}`,
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        borderRadius: `24px 24px 24px 24px`,
        WebkitBackdropFilter: 'blur(20px)', // For Safari support
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.1)} 0%, transparent 100%)`,
          borderRadius: 'inherit',
          pointerEvents: 'none'
        },
        '&:hover': {
          boxShadow: shadows[6],
          transform: 'translateY(-2px)',
          background: `linear-gradient(145deg, ${alpha(palette.primary.light, 0.1)} 20%, ${alpha(palette.secondary.light, 0.1)} 100%)`,
        },
      }
    }
  },
  MuiFormLabel: {
    styleOverrides: {
      asterisk: {
        fontSize: '1.5rem',  // Increased size for the asterisk
        color: palette.error.dark,  // Keep the error color
        '&$error': {
          color: palette.error.dark
        },
        marginLeft: 2,
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        // padding: '24px',
        position: 'relative',
        zIndex: 1
      },
      title: {
        fontSize: '1.25rem',
        fontWeight: 600,
      },
      subheader: {
        fontSize: '0.875rem',
        color: alpha(palette.text.secondary, 0.9),
      }
    }
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        // padding: '24px',
        position: 'relative',
        zIndex: 1,
        '&:last-child': {
          paddingBottom: '24px',
        }
      }
    }
  },
  MuiCardActions: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
        justifyContent: 'flex-end',
        position: 'relative',
        zIndex: 1
      }
    }
  }
};

let theme = createTheme({
  name: 'lavender',
  palette,
  typography,
  shape,
  spacing,
  shadows,
  components
});
theme = responsiveFontSizes(theme);

export default theme;
